import { useCallback } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { ROUTES } from '../types/global/routes.types';
import { navigate } from '../redux/navigator/action';
import { setmedia, setPlayerQuery } from '../redux/project/actions';
import { MediaType } from '../redux/project/types';
import { setMute } from '../redux/player/action';
import {
    currentVideoSelector,
    horizontalPlayerSelector,
    selectedActSelector,
    verticalPlayerSelector,
} from '../pages/Dashboard/selectors';

export interface UseLogoHomeNavigationOptions {
    /** Runs at the start of a logo click (e.g. collapse drawers). */
    onPrepare?: () => void;
    /** Runs after clearing playback when exiting to /watch (e.g. close the video modal). */
    onClosePlayer?: () => void;
    /** Custom navigation when not exiting the watch player. */
    onDefaultNavigate?: () => void;
}

export function useLogoHomeNavigation(options: UseLogoHomeNavigationOptions = {}) {
    const { onPrepare, onClosePlayer, onDefaultNavigate } = options;
    const dispatch = useDispatch();
    const routerNavigate = useNavigate();
    const { pathname } = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const playerOpen = useSelector(horizontalPlayerSelector);
    const actPlayerOpen = useSelector(verticalPlayerSelector);
    const currentVideo = useSelector(currentVideoSelector);
    const selectedAct = useSelector(selectedActSelector);

    const isOnWatchRoute =
        pathname === ROUTES.WATCH ||
        pathname.startsWith(`${ROUTES.WATCH}/`) ||
        pathname === ROUTES.ESWATCH_VIDEO ||
        pathname.startsWith(`${ROUTES.ESWATCH_VIDEO}/`);

    const closeWatchMediaPlayer = useCallback(
        (mediaType: MediaType) => {
            dispatch(
                setPlayerQuery({
                    isSequential: false,
                    isSinglePreview: false,
                }),
            );
            dispatch(
                setmedia({
                    media: null,
                    mediaType,
                    submissionObject: undefined,
                    playlistType: 'recommendation',
                    clearCustomSuggestion: true,
                }),
            );
            if (isOnWatchRoute) {
                setSearchParams({}, { replace: true });
            }
            dispatch(setMute(true));
        },
        [dispatch, isOnWatchRoute, setSearchParams],
    );

    const shouldExitToWatchHub = useCallback(() => {
        const isWatchingFromQuery = !!(searchParams.get('id') || searchParams.get('playlist'));
        const isVideoPlaying = playerOpen || actPlayerOpen || !!currentVideo || !!selectedAct;
        return (isOnWatchRoute && isWatchingFromQuery) || isVideoPlaying;
    }, [actPlayerOpen, currentVideo, isOnWatchRoute, playerOpen, searchParams, selectedAct]);

    const handleLogoClick = useCallback(
        (event?: React.MouseEvent) => {
            event?.preventDefault();
            onPrepare?.();

            if (shouldExitToWatchHub()) {
                routerNavigate(ROUTES.ESWATCH_VIDEO, { replace: true });
                if (actPlayerOpen && !playerOpen && !currentVideo) {
                    closeWatchMediaPlayer('act');
                } else {
                    closeWatchMediaPlayer('video');
                }
                onClosePlayer?.();
                return;
            }

            if (onDefaultNavigate) {
                onDefaultNavigate();
                return;
            }

            dispatch(navigate({ routes: ROUTES.FEED, skipAuthentication: true }));
        },
        [
            actPlayerOpen,
            closeWatchMediaPlayer,
            currentVideo,
            dispatch,
            onClosePlayer,
            onDefaultNavigate,
            onPrepare,
            playerOpen,
            routerNavigate,
            shouldExitToWatchHub,
        ],
    );

    return { handleLogoClick, shouldExitToWatchHub };
}
